import * as React from 'react';
import { CSSProperties, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { topNavBarHeight, leftBarWidth } from './constants';
import { logOut, useDispatch, useSelector } from '../../state';
import { Routes } from '../../util/constants';
import { useNavigate } from 'react-router-dom';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LanguageIcon from '@mui/icons-material/Language';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import AddCardIcon from '@mui/icons-material/AddCard';
import ListItemIcon from '@mui/material/ListItemIcon';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import OrderPlusIcon from '../Icons/OrderPlusIcon';
import InvoicePlusIcon from '../Icons/InvoicePlusIcon';
import EstimatePlusIcon from '../Icons/EstimatePlusIcon';
import PackingSlipPlusIcon from '../Icons/PackingSlipPlusIcon';
import VendorSlipIcon from '../Icons/VendorSlipIcon';
import MainAppLogoIcon from '../Icons/MainAppLogoIcon';
import { useCompanyLogoUrl } from '../../util';
import { openCustomerPopupModal } from '../../state/customerPopupModalSlice';
import { openVendorPopupModal } from '../../state/vendorPopupModalSlice';
import { AuthError } from 'aws-amplify/auth';
import { openOrderPopupModal } from '../../state/orderPopupModalSlice';
import { openAccountModal } from '../../state/accountModalSlice';
import { openInvoicePopupModal } from '../../state/invoicePopupModalSlice';
import { openPackingSlipPopupModal } from '../../state/packingSlipPopupModalSlice';

export default function TopNavBar({
  setLoading,
}: {
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}): JSX.Element {
  const theme = useTheme();
  const { t, i18n } = useTranslation([], { keyPrefix: 'team.PageLayout.TB' });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const companyLogoUrl = useCompanyLogoUrl();
  const { user } = useSelector(({ userSlice }) => userSlice);

  const iconStyle: CSSProperties = {
    fontSize: '24px',
    color: theme.palette.primary.main,
  };

  const MenuItemHeight = 44;

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElNew, setAnchorElNew] = useState<null | HTMLElement>(null);
  const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);

  const handleOpenNewMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNew(event.currentTarget);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNewMenu = () => {
    setAnchorElNew(null);
  };

  const handleOpenLangMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElLang(event.currentTarget);
  };

  const handleCloseLangMenu = () => {
    setAnchorElLang(null);
  };

  const handleChangeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    handleCloseLangMenu();
  };

  const languageLabels: Record<string, string> = {
    en: 'English',
    es: 'Español',
  };

  const currentLanguage =
    languageLabels[i18n.language] ??
    languageLabels[i18n.language?.split('-')[0] ?? 'en'] ??
    'English';

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleAccountMenu = () => {
    handleCloseUserMenu();
    dispatch(openAccountModal({ resetState: true }));
  };

  const handleCreateCustomer = () => {
    dispatch(openCustomerPopupModal());
    handleCloseNewMenu();
  };

  const handleCreateVendor = () => {
    dispatch(openVendorPopupModal());
    handleCloseNewMenu();
  };

  const handleCreateOrder = () => {
    dispatch(openOrderPopupModal());
    handleCloseNewMenu();
  };

  const handleCreateInvoice = () => {
    dispatch(openInvoicePopupModal());
    handleCloseNewMenu();
  };

  const handleCreatePackingSlip = () => {
    dispatch(openPackingSlipPopupModal());
    handleCloseNewMenu();
  };

  const handleLogOut = async () => {
    try {
      setLoading?.(true);
      await dispatch(logOut());
      setLoading?.(false);
      navigate(Routes.LOGIN);
    } catch (error) {
      console.error(error);
    }
  };

  const userName = React.useMemo(() => {
    if (user === 'init' || user === 'loading') return '';
    if (user instanceof AuthError) return '';
    return user.username;
  }, [user]);

  return (
    <AppBar
      elevation={4}
      position="relative"
      sx={{ height: topNavBarHeight, zIndex: (theme) => theme.zIndex.appBar }}
    >
      <Container
        maxWidth={false}
        disableGutters
        sx={{ paddingLeft: 0, paddingRight: '24px' }}
      >
        <Toolbar disableGutters>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: leftBarWidth,
              height: topNavBarHeight,
              flexShrink: 0,
              boxSizing: 'border-box',
              p: '5px',
            }}
          >
            {companyLogoUrl ? (
              <Box
                component="img"
                src={companyLogoUrl}
                alt="Company logo"
                sx={{
                  width: 54,
                  height: 54,
                  objectFit: 'contain',
                  borderRadius: 3,
                }}
              />
            ) : (
              <MainAppLogoIcon
                style={{
                  width: 54,
                  height: 54,
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              />
            )}
          </Box>
          <Box sx={{ ml: 'auto' }}>
            {/* Language */}
            <Button
              variant="outlined"
              size="small"
              sx={{ mr: 2 }}
              startIcon={<LanguageIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={handleOpenLangMenu}
            >
              {currentLanguage}
            </Button>
            <Menu
              elevation={4}
              MenuListProps={{ sx: { paddingTop: 0, paddingBottom: 0 } }}
              sx={{ mt: '45px' }}
              id="language-menu"
              anchorEl={anchorElLang}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElLang)}
              onClose={handleCloseLangMenu}
            >
              {Object.entries(languageLabels).map(([code, label]) => (
                <MenuItem
                  key={code}
                  selected={
                    i18n.language === code ||
                    i18n.language?.startsWith(code + '-')
                  }
                  onClick={() => handleChangeLanguage(code)}
                  sx={{ paddingY: 0, height: MenuItemHeight }}
                >
                  <Typography textAlign="center" color="primary">
                    {label}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
            {/* New Button */}
            <Button
              variant="contained"
              endIcon={<ArrowDropDownIcon />}
              sx={{ mr: 2 }}
              size="small"
              onClick={handleOpenNewMenu}
            >
              {t('New')}
            </Button>
            <Menu
              elevation={4}
              MenuListProps={{ sx: { paddingTop: 0, paddingBottom: 0 } }}
              sx={{ mt: '45px' }}
              id="new-appbar"
              anchorEl={anchorElNew}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElNew)}
              onClose={handleCloseNewMenu}
            >
              <MenuItem
                divider
                key={'Customer'}
                onClick={handleCreateCustomer}
                sx={{ paddingY: 0, height: MenuItemHeight }}
              >
                <ListItemIcon>
                  <PersonAddIcon color="primary" />
                </ListItemIcon>
                <Typography textAlign="center" color="primary">
                  {' '}
                  {t('Customer')}
                </Typography>
              </MenuItem>
              <MenuItem
                divider
                key={'Vendor'}
                onClick={handleCreateVendor}
                sx={{ paddingY: 0, height: MenuItemHeight }}
              >
                <ListItemIcon>
                  <VendorSlipIcon style={iconStyle} />
                </ListItemIcon>
                <Typography textAlign="center" color="primary">
                  {t('Vendor')}
                </Typography>
              </MenuItem>
              <MenuItem
                divider
                key={'Order'}
                onClick={handleCreateOrder}
                sx={{ paddingY: 0, height: MenuItemHeight }}
              >
                <ListItemIcon>
                  <OrderPlusIcon style={iconStyle} />
                </ListItemIcon>
                <Typography textAlign="center" color="primary">
                  {t('Sales Order')}
                </Typography>
              </MenuItem>
              <MenuItem
                divider
                key={'Invoice'}
                onClick={handleCreateInvoice}
                sx={{ paddingY: 0, height: MenuItemHeight }}
              >
                <ListItemIcon>
                  <InvoicePlusIcon style={iconStyle} />
                </ListItemIcon>
                <Typography textAlign="center" color="primary">
                  {t('Invoice')}
                </Typography>
              </MenuItem>
              <MenuItem
                divider
                key={'Packing Slip'}
                onClick={handleCreatePackingSlip}
                sx={{ paddingY: 0, height: MenuItemHeight }}
              >
                <ListItemIcon>
                  <PackingSlipPlusIcon style={iconStyle} />
                </ListItemIcon>
                <Typography textAlign="center" color="primary">
                  {t('Packing Slip')}
                </Typography>
              </MenuItem>
              <MenuItem
                divider
                key={'Payment'}
                onClick={handleCloseNewMenu}
                sx={{ paddingY: 0, height: MenuItemHeight }}
              >
                <ListItemIcon>
                  <AddCardIcon color="primary" />
                </ListItemIcon>
                <Typography textAlign="center" color="primary">
                  {t('Payment')}
                </Typography>
              </MenuItem>
              <MenuItem
                key={'Estimate'}
                onClick={handleCloseNewMenu}
                sx={{ paddingY: 0, height: MenuItemHeight }}
              >
                <ListItemIcon>
                  <EstimatePlusIcon style={iconStyle} />
                </ListItemIcon>
                <Typography textAlign="center" color="primary">
                  {t('Estimate')}
                </Typography>
              </MenuItem>
            </Menu>
            {/* New Messages Icon Button */}
            <IconButton sx={{ mr: 2 }} color="primary" size="small">
              <Badge variant="dot" color="error">
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
            {/* Avatar */}
            <Tooltip title="Open settings">
              <IconButton
                size="small"
                onClick={handleOpenUserMenu}
                sx={{ p: 0 }}
              >
                {/* TODO  Avatar Picture will be here */}
                <Avatar sx={{ width: 32, height: 32 }}>
                  {userName?.charAt(0)?.toUpperCase()}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              MenuListProps={{ sx: { paddingTop: 0, paddingBottom: 0 } }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem
                divider
                key={'Account'}
                onClick={handleAccountMenu}
                sx={{ paddingY: 0, height: MenuItemHeight }}
              >
                <ListItemIcon>
                  <AccountCircleIcon color="primary" />
                </ListItemIcon>
                <Typography textAlign="center" color="primary">
                  {t('Account')}
                </Typography>
              </MenuItem>
              <MenuItem
                divider
                key={'Team'}
                onClick={handleCloseUserMenu}
                sx={{ paddingY: 0, height: MenuItemHeight }}
              >
                <ListItemIcon>
                  <PeopleAltOutlinedIcon color="primary" />
                </ListItemIcon>
                <Typography textAlign="center" color="primary">
                  {t('Team')}
                </Typography>
              </MenuItem>
              <MenuItem
                divider
                key={'Sign Out'}
                onClick={handleLogOut}
                sx={{ paddingY: 0, height: MenuItemHeight }}
              >
                <ListItemIcon>
                  <LogoutOutlinedIcon color="primary" />
                </ListItemIcon>
                <Typography textAlign="center" color="primary">
                  {t('Sign Out')}
                </Typography>
              </MenuItem>
              {/* TODO Verson of app should be dynamic. */}
              <Typography
                textAlign="center"
                fontSize="10px"
                sx={{ padding: '2px' }}
              >
                Version: {process.env.REACT_APP_GIT_SHA}
              </Typography>
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
